import { getWidgetItemFromShopItemInfo } from "@/abilities/shop/features/generic/helper/get-widget-item-from-shop-item-info";
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { ShopArticleContext } from "@/shared/environment/widgets/widget-context";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import {
    GenericShopItemInfoDocument,
    GenericShopItemInfoQuery,
    GenericShopItemInfoQueryVariables,
    ShopItemInfoInput,
} from "@/shared/services/graphql/generated/consumer-graph-types";

/**
 * Translates the widget's article context into the lookup the Generic Shop
 * Interface understands.
 *
 * `ShopItemInfoInput` knows an asset and a product; `mechanicId` and
 * `assemblyId` have no counterpart and are dropped rather than smuggled into
 * another field. Empty values are omitted so the shop sees "not given" instead
 * of an empty string.
 */
export const buildShopItemInfoInput = (articleId: string, context?: ShopArticleContext): ShopItemInfoInput => {
    const input: ShopItemInfoInput = { articleId };

    if (context?.assetId) {
        input.assetId = context.assetId;
    }
    if (context?.productId) {
        input.productId = context.productId;
    }

    return input;
};

/**
 * Reads price and stock for one article and normalizes it for the widget.
 *
 * Queried imperatively through `consumerClient`, the way the FDI, Salesforce and
 * SAP adapters read (`use-fdi-shop.ts`). The generated `useQuery`/`useLazyQuery`
 * composables are deliberately not used here: they only tear down their
 * `ObservableQuery` subscription and watchers inside a component instance
 * (`vm && onBeforeUnmount(...)`), and these loaders run from plain async
 * functions, so each call would leak one of each.
 *
 * `fetchPolicy` is pinned rather than inherited. `consumerClient` already sets
 * `network-only` as its client-wide default (`consumer-client.ts:16-23`), but
 * that default inverts the documented convention and is expected to change
 * (tech issue 0009 / QIT-7029) — price and stock are live shop data and must not
 * start answering from the cache when it does. Apollo deduplicates identical
 * in-flight queries, so several widgets asking for the same article at once
 * still produce one request.
 *
 * A `null` result is not an error: the shop answers `shopItemInfo: null` for an
 * article it does not carry (`ART-NOT-FOUND`), and the widget renders that as
 * "Article not found" — the same state a failed query produces.
 */
const loadOne = async (input: ShopItemInfoInput): Promise<ShopCore.WidgetItem | null> => {
    try {
        const { data } = await consumerClient.query<GenericShopItemInfoQuery, GenericShopItemInfoQueryVariables>({
            query: GenericShopItemInfoDocument,
            variables: { input },
            fetchPolicy: "network-only",
        });

        return data?.shopItemInfo ? getWidgetItemFromShopItemInfo(data.shopItemInfo) : null;
    } catch (error) {
        // The article id is passed as an argument, not interpolated: a format
        // specifier inside it would otherwise forge the log line (semgrep
        // javascript.lang.security.audit.unsafe-formatstring).
        console.warn("Generic shop: item info could not be resolved for article", input.articleId, error);
        return null;
    }
};

/**
 * Reads price and stock for a set of articles (QIT-7074).
 *
 * **This is the batch seam.** The Generic Shop Interface has no batch field yet
 * — `shopItemInfos` is gap 5 of the QIT-6923 asks — and aliasing one
 * `shopItemInfo` per article into a single document is not a workaround either,
 * because the gateway rejects documents with more than 15 aliases
 * (`docs/tech-issues/0041-*`). So the transport is N parallel single queries,
 * while callers already speak in sets: when the batch field lands, only this
 * function's body changes.
 *
 * Failures are contained per article — one unreachable article yields `null` in
 * its slot instead of failing the whole set. The result always has one entry
 * per input, in the order asked for.
 */
export const loadGenericShopItemInfos = async (
    inputs: ShopItemInfoInput[]
): Promise<Array<ShopCore.WidgetItem | null>> => Promise.all(inputs.map(loadOne));

/** Single-article convenience wrapper around {@link loadGenericShopItemInfos}. */
export const loadGenericShopItemInfo = async (input: ShopItemInfoInput): Promise<ShopCore.WidgetItem | null> =>
    (await loadGenericShopItemInfos([input]))[0] ?? null;
